import { z } from 'zod';

/**
 * DEMO VERİ MODU — sözleşme (Faz 8).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Ürün kararı: boş panel ürünü anlatmıyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Hesabını henüz bağlamamış kullanıcı bugün 24 rotanın hepsinde boş liste
 * görüyor. Demo modu, o kullanıcıya kendi workspace'inde GERÇEK satırlar
 * yazıyor: metrikler `metrics_daily`de, kampanyalar `campaigns`te. Yani
 * kullanıcının gezdiği ekranlar sahte bir yol değil, ürünün kendisi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Veri ÜRETECİ burada değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosya yalnız uç sözleşmesi — `apps/web` bunu okuyor. Sayıları üreten
 * ~800 satır `demo-data.ts`te ve kök barrel'dan export EDİLMİYOR: tarayıcı
 * bundle'ında işi yok. (`mail.ts` ve `template-seed.ts` ile aynı gerekçe.)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Demo veri GERÇEK veriyle karışabilir — ve bu bilinçli olarak
 *    kullanıcının kararına bırakıldı
 * ═══════════════════════════════════════════════════════════════════════════
 * Kullanıcı Meta'yı bağladığında demo satırları OTOMATİK silinmiyor. Sessiz
 * silme, "birazdan bakarım" diyen kullanıcının ekranını haber vermeden
 * boşaltırdı. Bunun yerine `GET /demo` yanıtı `active: true` diyor, arayüz
 * "demo veriyi kaldır" diyen bir şerit gösteriyor ve silme `DELETE /demo`
 * ile açıkça isteniyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Sabit
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Demo hiyerarşideki her `platformRef` bu önekle başlıyor.
 *
 * Üreteçte (`demo-data.ts`) değil BURADA: arayüzün de "bu satır demo mu"
 * sorusunu sorması gerekiyor. Bağlantılar ekranı bunu okuyup ham kimliği
 * (`demo_act_meta`) gizliyor ve yerine "örnek veri" rozeti basıyor —
 * kullanıcı o dizeyi çevrilmemiş bir sözlük anahtarı sanıyordu. Üreteci
 * tarayıcı bundle'ına sokmamak için ön ek buradan okunuyor, `demo-data.ts`
 * de aynı değeri buradan alıp yeniden export ediyor.
 */
export const DEMO_REF_PREFIX = 'demo_';

// ═══════════════════════════════════════════════════════════════════════════
// Durum
// ═══════════════════════════════════════════════════════════════════════════

export interface DemoDataCounts {
  connections: number;
  accounts: number;
  campaigns: number;
  adSets: number;
  ads: number;
  creatives: number;
  keywords: number;
  searchTerms: number;
  metrics: number;
  /// Faz 6 — atıf penceresi satırları (`metric_attribution_daily`).
  /// Yalnız pencere kırılımı veren platformda doğuyor; bugün Meta.
  attribution: number;
}

export interface DemoStatusResponse {
  /** Bu workspace'te demo veri var mı. */
  active: boolean;
  /**
   * Kullanıcının GERÇEK (demo olmayan) bir reklam bağlantısı var mı.
   *
   * Arayüz iki bayrağı birlikte okuyor: ikisi de `true` ise "gerçek veriniz
   * geldi, demo veriyi kaldırabilirsiniz" şeridi gösteriliyor.
   */
  hasRealConnection: boolean;
  /** Demo veri yazıldıysa kapsadığı ilk/son gün — 'YYYY-MM-DD'. */
  startDate: string | null;
  endDate: string | null;
  counts: DemoDataCounts | null;
}

export interface DemoProvisionResponse {
  counts: DemoDataCounts;
  startDate: string;
  endDate: string;
  /** İkinci çalıştırmada `false`: satırlar zaten vardı, içerik tazelendi. */
  created: boolean;
}

export interface DemoClearResponse {
  /** Silinen bağlantı sayısı. Çocuk satırlar CASCADE ile gidiyor. */
  removedConnections: number;
  /** Silinen kreatif sayısı — bağlantıya bağlı değiller, ayrı siliniyor. */
  removedCreatives: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Girdi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Demo veri yazma isteği.
 *
 * `days` neden ayarlanabilir: 180 gün varsayılan (90 günlük aralığın kendi
 * önceki dönemiyle karşılaştırılabilmesi için gereken en az derinlik), ama
 * hızlı bir deneme için 30 gün yeter ve 10.000 satır yerine 1.700 satır yazar.
 */
export const demoProvisionSchema = z.object({
  days: z.coerce.number().int().min(7).max(365).default(180),
});
export type DemoProvisionInput = z.infer<typeof demoProvisionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Hatalar
// ═══════════════════════════════════════════════════════════════════════════

export const DemoErrorCode = {
  /**
   * Bu workspace'te GERÇEK bir reklam bağlantısı var → demo veri yazılmaz.
   *
   * 409, 400 değil: istek geçerli, çakışan bir durum var. Karıştırmak
   * yasak çünkü `metrics_daily` iki kaynağı ayırt edemez — kullanıcı gerçek
   * ROAS'ının yanında uydurma bir ROAS görür ve hangisinin hangisi olduğunu
   * anlayamaz. Bütçe kararları o karışık sayıya bakarak veriliyor.
   */
  REAL_CONNECTION_EXISTS: 'REAL_CONNECTION_EXISTS',
  /** Silinecek demo veri yok. */
  DEMO_DATA_NOT_FOUND: 'DEMO_DATA_NOT_FOUND',
} as const;
export type DemoErrorCode = (typeof DemoErrorCode)[keyof typeof DemoErrorCode];
